import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, FileText, Users, TrendingUp, AlertCircle, CheckCircle, Clock, XCircle, DollarSign, Shield, UserCog, Mail, Settings, Trash2, Power, BarChart3, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import ConfirmModal from '../components/ConfirmModal';
import { formatearFechaHoraCaracas } from '../utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ESTADO_CONFIG = {
  solicitud_iniciada: { label: 'Solicitud Iniciada', color: 'bg-blue-500', icon: Clock },
  formulario_1_pendiente: { label: 'Form. 1 Pendiente', color: 'bg-blue-500', icon: FileText },
  cita_pendiente: { label: 'Cita Pendiente', color: 'bg-blue-500', icon: Clock },
  cita_agendada: { label: 'Cita Agendada', color: 'bg-yellow-500', icon: CheckCircle },
  en_evaluacion: { label: 'En Evaluación', color: 'bg-yellow-500', icon: AlertCircle },
  cliente_retenido: { label: 'Cliente Retenido ✓', color: 'bg-green-500', icon: CheckCircle },
  retiro_confirmado: { label: 'Retiro Confirmado', color: 'bg-orange-500', icon: AlertCircle },
  formulario_2_pendiente: { label: 'Form. 2 Pendiente', color: 'bg-blue-500', icon: FileText },
  calculo_pendiente: { label: 'Cálculo Pendiente', color: 'bg-yellow-500', icon: Clock },
  firma_pendiente: { label: 'Firma Pendiente', color: 'bg-orange-500', icon: FileText },
  pago_autorizado: { label: 'Pago Autorizado', color: 'bg-orange-500', icon: CheckCircle },
  completado: { label: 'Completado', color: 'bg-green-500', icon: CheckCircle },
  cancelado: { label: 'Cancelado', color: 'bg-red-500', icon: XCircle }
};

const ROLE_LABELS = {
  admin: 'Administrador',
  gerencial: 'Gerencial',
  financiero: 'Financiero',
  operador: 'Operador'
};

export default function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [expedientes, setExpedientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, userId: null, userName: '' });
  const [deleteExpedienteModal, setDeleteExpedienteModal] = useState({ isOpen: false, expediente: null });
  const [serviceTimer, setServiceTimer] = useState(null); // null, o timestamp cuando se conectó Google
  const [timeRemaining, setTimeRemaining] = useState(0);

  useEffect(() => {
    if (user) {
      setGoogleConnected(!!user.google_tokens);
      loadStats();
      loadExpedientes();
      if (user.role === 'admin') {
        loadUsuarios();
      }
    }
  }, [user]);


  // Temporizador de servicios
  useEffect(() => {
    // Verificar si hay un timer guardado en localStorage
    const savedTimer = localStorage.getItem('google_service_timer');
    if (savedTimer) {
      const timerStart = parseInt(savedTimer);
      const now = Date.now();
      const elapsed = now - timerStart;
      const fiveMinutes = 5 * 60 * 1000;
      
      if (elapsed < fiveMinutes) {
        setServiceTimer(timerStart);
      } else {
        localStorage.removeItem('google_service_timer');
      }
    }
  }, []);

  // Countdown del temporizador
  useEffect(() => {
    if (!serviceTimer) return;
    
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - serviceTimer;
      const fiveMinutes = 5 * 60 * 1000;
      const remaining = fiveMinutes - elapsed;
      
      if (remaining <= 0) {
        setServiceTimer(null);
        setTimeRemaining(0);
        localStorage.removeItem('google_service_timer');
        toast.success('✅ Servicios de Google listos para usar');
      } else {
        setTimeRemaining(Math.ceil(remaining / 1000));
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [serviceTimer]);


  const loadStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/stats/dashboard`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadExpedientes = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/expedientes`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setExpedientes(data);
      }
    } catch (error) {
      console.error('Error loading expedientes:', error);
    }
  };

  const loadUsuarios = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/usuarios`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUsuarios(data);
      }
    } catch (error) {
      console.error('Error loading usuarios:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
      onLogout();
      toast.success('Sesión cerrada');
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const connectGoogle = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/google/auth-url`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.auth_url;
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al conectar con Google');
    }
  };

  const searchByCedula = async () => {
    if (!searchQuery) return;
    try {
      // FIX: Usar XMLHttpRequest para evitar conflicto con rrweb-recorder
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${BACKEND_URL}/api/expedientes/buscar/cedula/${encodeURIComponent(searchQuery)}`, true);
        xhr.withCredentials = true;
        
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve({ ok: true, data: JSON.parse(xhr.responseText) });
            } catch (e) {
              reject(new Error('Respuesta inválida del servidor'));
            }
          } else {
            resolve({ ok: false, status: xhr.status });
          }
        };
        
        xhr.onerror = function() {
          reject(new Error('Error de conexión'));
        };
        
        xhr.send();
      });

      if (result.ok) {
        setSearchResult(result.data);
      } else {
        toast.error('No se encontró expediente con esa cédula');
        setSearchResult(null);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al buscar');
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/usuarios/${userId}/role?role=${newRole}`, {
        method: 'PATCH',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Rol actualizado exitosamente');
        loadUsuarios();
      } else {
        toast.error('Error al actualizar rol');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al actualizar rol');
    }
  };

  const toggleUserActive = async (userId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/usuarios/${userId}/toggle-active`, {
        method: 'PATCH',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.active ? 'Usuario activado' : 'Usuario bloqueado');
        loadUsuarios();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al cambiar estado');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cambiar estado');
    }
  };


  const [deletingExpediente, setDeletingExpediente] = useState(false);

  const handleDeleteExpediente = async () => {
    if (!deleteExpedienteModal.expediente) return;
    setDeletingExpediente(true);
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/expedientes/${deleteExpedienteModal.expediente.id}/eliminar`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || 'Expediente eliminado');
        setDeleteExpedienteModal({ isOpen: false, expediente: null });
        loadExpedientes();
      } else {
        toast.error(data.detail || 'Error al eliminar expediente');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar expediente');
    } finally {
      setDeletingExpediente(false);
    }
  };


  const resetUserPassword = async (userId) => {
    const nuevaPassword = prompt('Ingresa la nueva contraseña:');
    if (!nuevaPassword) return;

    if (nuevaPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/usuarios/${userId}/resetear-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ nueva_password: nuevaPassword })
      });

      if (response.ok) {
        toast.success('Contraseña reseteada exitosamente');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al resetear contraseña');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al resetear contraseña');
    }
  };

  const abrirModalEliminar = (userId, userName) => {
    setDeleteModal({ isOpen: true, userId, userName });
  };

  const confirmarEliminarUsuario = async () => {
    const { userId } = deleteModal;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/usuarios/${userId}/eliminar`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Usuario eliminado exitosamente');
        loadUsuarios();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al eliminar usuario');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar usuario');
    }
  };



  const isAdmin = user?.role === 'admin';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      <header className="bg-gray-900/80 backdrop-blur-xl border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          {/* Primera fila: Logo, Título y Usuario */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <img src="/kompa-logo.png" alt="Kompa" className="h-12 w-auto" />
              <div>
                <h1 className="text-2xl font-bold text-white">Sistema de Retiros</h1>
                {user && (
                  <p className="text-sm text-gray-400">{ROLE_LABELS[user.role]}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-white">{user?.name}</p>
                <p className="text-xs text-gray-400">{user?.email}</p>
              </div>
              <Button
                data-testid="logout-button"
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="border-gray-700 bg-gray-800 text-white hover:bg-red-600 hover:border-red-600 transition-colors"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Salir
              </Button>
            </div>
          </div>

          {/* Segunda fila: Botones de navegación */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Botones principales - Siempre visibles para Admin/Gerencial */}
            {(isAdmin || user?.role === 'gerencial') && (
              <>
                <Button
                  onClick={() => navigate('/analytics')}
                  variant="outline"
                  size="sm"
                  className="border-yellow-500 bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500 hover:text-black transition-colors font-semibold"
                  title="Ver Indicadores y Métricas"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Indicadores
                </Button>
                <Button
                  onClick={() => navigate('/gestor-citas')}
                  variant="outline"
                  size="sm"
                  className="border-blue-500 bg-blue-500/10 text-blue-500 hover:bg-blue-500 hover:text-white transition-colors font-semibold"
                  title="Gestor de Citas"
                >
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Gestor Citas
                </Button>
                <Button
                  onClick={() => navigate('/admin/clientes-sin-cita')}
                  variant="outline"
                  size="sm"
                  className="border-orange-500 bg-orange-500/10 text-orange-500 hover:bg-orange-500 hover:text-white transition-colors font-semibold"
                  title="Clientes Sin Cita Agendada"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Sin Cita
                </Button>
              </>
            )}

            {/* Separador visual */}
            {isAdmin && (
              <div className="h-8 w-px bg-gray-700 mx-2"></div>
            )}

            {/* Botones de admin específicos */}
            {isAdmin && (
              <>
                <Button
                  onClick={() => navigate('/admin/error-logs')}
                  variant="outline"
                  size="sm"
                  className="border-red-500 bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white transition-colors"
                  title="Ver Logs de Errores"
                >
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Error Logs
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      const response = await fetch(`${BACKEND_URL}/api/emails/check-now`, {
                        method: 'POST',
                        credentials: 'include'
                      });
                      if (response.ok) {
                        toast.success('Revisión de emails completada');
                      } else {
                        toast.error('Error al revisar emails');
                      }
                    } catch (error) {
                      toast.error('Error al revisar emails');
                    }
                  }}
                  variant="outline"
                  size="sm"
                  className="border-gray-700 bg-gray-800 text-gray-300 hover:bg-blue-600 hover:border-blue-600 hover:text-white transition-colors"
                  title="Revisar emails ahora"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Revisar Emails
                </Button>
                <Button
                  onClick={() => navigate('/email-templates')}
                  variant="outline"
                  size="sm"
                  className="border-gray-700 bg-gray-800 text-gray-300 hover:bg-purple-600 hover:border-purple-600 hover:text-white transition-colors"
                  title="Configurar Templates de Email"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Templates
                </Button>
                <Button
                  onClick={() => navigate('/email-tracking')}
                  variant="outline"
                  size="sm"
                  className="border-gray-700 bg-gray-800 text-gray-300 hover:bg-indigo-600 hover:border-indigo-600 hover:text-white transition-colors"
                  title="Ver Emails Procesados"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Tracking
                </Button>
                <Button
                  onClick={() => navigate('/monitoreo-emails')}
                  variant="outline"
                  size="sm"
                  className="border-gray-700 bg-gray-800 text-gray-300 hover:bg-green-600 hover:border-green-600 hover:text-white transition-colors"
                  title="Control de Monitoreo Automático"
                >
                  <Power className="w-4 h-4 mr-2" />
                  Monitoreo
                </Button>
                <Button
                  onClick={() => navigate('/blacklist-cedulas')}
                  variant="outline"
                  size="sm"
                  className="border-gray-700 bg-gray-800 text-gray-300 hover:bg-red-600 hover:border-red-600 hover:text-white transition-colors"
                  title="Blacklist de Cédulas"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Blacklist
                </Button>
                <Button
                  onClick={() => navigate('/configuracion')}
                  variant="outline"
                  size="sm"
                  className="border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700 hover:border-gray-600 hover:text-white transition-colors"
                  title="Configuración del Sistema"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Config
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Banner de Temporizador de Servicios */}
        {serviceTimer && timeRemaining > 0 && (
          <Card className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border-blue-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Clock className="w-8 h-8 text-blue-400 animate-pulse" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">⚙️ Inicializando Servicios de Google</h3>
                    <p className="text-sm text-gray-300">
                      Los servicios estarán completamente listos en <strong className="text-blue-400">{Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}</strong> minutos
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-blue-400">{Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}</div>
                  <p className="text-xs text-gray-400">minutos restantes</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!googleConnected && (
          <Card className="bg-gradient-to-r from-yellow-900/20 to-orange-900/20 border-yellow-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-white mb-1">Conecta tu cuenta de Google</h3>
                  <p className="text-sm text-gray-300">Necesitas autorizar Gmail y Calendar para usar el sistema</p>
                </div>
                <Button onClick={connectGoogle} className="bg-yellow-500 hover:bg-yellow-600 text-black">
                  Conectar Google
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <StatCard title="Total Expedientes" value={stats.total} icon={<FileText className="w-6 h-6" />} color="bg-blue-500" />
            <StatCard title="En Proceso" value={stats.activos} icon={<Clock className="w-6 h-6" />} color="bg-yellow-500" />
            <StatCard title="Retenidos" value={stats.retenidos} icon={<CheckCircle className="w-6 h-6" />} color="bg-green-500" />
            <StatCard title="Retirados" value={stats.retirados} icon={<TrendingUp className="w-6 h-6" />} color="bg-purple-500" />
            <StatCard title="Pagados" value={stats.pagados} icon={<DollarSign className="w-6 h-6" />} color="bg-emerald-500" />
          </div>
        )}

        <Tabs defaultValue="expedientes" className="w-full">
          <TabsList className={`grid w-full ${isAdmin ? 'grid-cols-5' : 'grid-cols-3'} bg-gray-800/50 p-1 rounded-xl border border-gray-700`}>
            <TabsTrigger value="expedientes" className="rounded-lg data-[state=active]:bg-yellow-500 data-[state=active]:text-black" data-testid="expedientes-tab">
              <FileText className="w-4 h-4 mr-2" />
              Expedientes
            </TabsTrigger>
            <TabsTrigger value="formulario" className="rounded-lg data-[state=active]:bg-yellow-500 data-[state=active]:text-black" data-testid="formulario-tab">
              <FileText className="w-4 h-4 mr-2" />
              Formulario
            </TabsTrigger>
            <TabsTrigger value="buscar" className="rounded-lg data-[state=active]:bg-yellow-500 data-[state=active]:text-black" data-testid="buscar-tab">
              <Users className="w-4 h-4 mr-2" />
              Buscar
            </TabsTrigger>
            {isAdmin && (
              <>
                <TabsTrigger value="usuarios" className="rounded-lg data-[state=active]:bg-yellow-500 data-[state=active]:text-black" data-testid="usuarios-tab">
                  <UserCog className="w-4 h-4 mr-2" />
                  Usuarios
                </TabsTrigger>
                <TabsTrigger value="admin" className="rounded-lg data-[state=active]:bg-yellow-500 data-[state=active]:text-black" data-testid="admin-tab">
                  <Shield className="w-4 h-4 mr-2" />
                  Administración
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Formulario Tab */}
          <TabsContent value="formulario" className="mt-6">
            <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Link del Formulario de Solicitud</CardTitle>
                <CardDescription className="text-gray-400">
                  Comparte este enlace con los solicitantes para que inicien su proceso de retiro
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-gray-900/50 rounded-xl border border-gray-700">
                  <p className="text-sm text-gray-400 mb-2">URL del Formulario:</p>
                  <div className="flex gap-2">
                    <Input
                      value={`${window.location.origin}/formulario-1`}
                      readOnly
                      className="bg-gray-800 border-gray-700 text-white font-mono text-sm"
                    />
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/formulario-1`);
                        toast.success('Link copiado al portapapeles');
                      }}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black"
                    >
                      Copiar
                    </Button>
                  </div>
                </div>

                <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-xl">
                  <h4 className="text-white font-semibold mb-2">¿Cómo funciona?</h4>
                  <ol className="text-sm text-gray-300 space-y-1 list-decimal list-inside">
                    <li>Comparte el link con el solicitante por email o WhatsApp</li>
                    <li>El solicitante llena el formulario con sus datos</li>
                    <li>Sube los documentos requeridos (cédula, comprobantes, etc.)</li>
                    <li>Al enviar, se crea automáticamente un expediente</li>
                    <li>El expediente aparecerá en la lista con estado "Cita Pendiente"</li>
                  </ol>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => window.open('/formulario-1', '_blank')}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                  >
                    Vista Previa del Formulario
                  </Button>
                  <Button
                    onClick={() => {
                      const subject = 'Formulario de Solicitud de Retiro - Kompa';
                      const body = `Hola,\n\nPara iniciar tu proceso de retiro, por favor completa el siguiente formulario:\n\n${window.location.origin}/formulario-1\n\nSaludos,\nEquipo Kompa`;
                      window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                    }}
                    variant="outline"
                    className="flex-1 border-gray-700 text-white hover:bg-gray-700"
                  >
                    Enviar por Email
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Expedientes Tab */}
          <TabsContent value="expedientes" className="mt-6">
            <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Expedientes Activos</CardTitle>
                <CardDescription className="text-gray-400">Gestión de todos los procesos de retiro</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4" data-testid="expedientes-list">
                  {expedientes.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No hay expedientes registrados</p>
                  ) : (
                    expedientes.map((exp) => (
                      <ExpedienteCard 
                        key={exp.id} 
                        expediente={exp} 
                        navigate={navigate}
                        isAdmin={user?.role === 'admin'}
                        onDelete={(expediente) => setDeleteExpedienteModal({ isOpen: true, expediente })}
                      />
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="buscar" className="mt-6">
            <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Buscar Expediente</CardTitle>
                <CardDescription className="text-gray-400">Busca por número de cédula del solicitante</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex gap-3">
                  <Input
                    data-testid="search-input"
                    placeholder="Ingresa número de cédula..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchByCedula()}
                    className="border-gray-700 bg-gray-900 text-white"
                  />
                  <Button data-testid="search-button" onClick={searchByCedula} className="bg-yellow-500 hover:bg-yellow-600 text-black">
                    Buscar
                  </Button>
                </div>
                {searchResult && (
                  <div data-testid="search-result">
                    <ExpedienteCard expediente={searchResult} detailed navigate={navigate} />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {isAdmin && (
            <>
              <TabsContent value="usuarios" className="mt-6 space-y-6">
                {/* Formulario de Creación */}
                <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-2xl text-white flex items-center gap-2">
                      <UserCog className="w-6 h-6 text-yellow-500" />
                      Crear Nuevo Usuario
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Crea usuarios para operadores, gerencial o financiero con acceso interno
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <CrearUsuarioForm onUsuarioCreado={loadUsuarios} />
                  </CardContent>
                </Card>

                {/* Lista de Usuarios */}
                <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-2xl text-white flex items-center gap-2">
                      <Users className="w-6 h-6 text-blue-500" />
                      Lista de Usuarios
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Administra roles, contraseñas y estado de usuarios del sistema
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4" data-testid="usuarios-list">
                      {usuarios.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">No hay usuarios registrados</p>
                      ) : (
                        usuarios.map((usuario) => (
                          <UsuarioCard 
                            key={usuario.id} 
                            usuario={usuario} 
                            onRoleChange={updateUserRole}
                            onToggleActive={toggleUserActive}
                            onResetPassword={resetUserPassword}
                            onEliminar={abrirModalEliminar}
                            currentUserId={user?.id}
                          />
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="admin" className="mt-6">
                <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-2xl text-white flex items-center gap-2">
                      <Shield className="w-6 h-6 text-yellow-500" />
                      Panel de Administración
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Configuración avanzada del sistema
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-400">Configuraciones administrativas próximamente...</p>
                  </CardContent>
                </Card>
              </TabsContent>
            </>
          )}
        </Tabs>
      </main>

      {/* Modal de Confirmación de Eliminación de Usuario */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, userId: null, userName: '' })}
        onConfirm={confirmarEliminarUsuario}
        title="Eliminar Usuario"
        message={`¿Estás seguro de que deseas ELIMINAR permanentemente al usuario "${deleteModal.userName}"?\n\nEsta acción NO se puede deshacer. Se eliminarán:\n• Datos del usuario\n• Todas sus sesiones activas\n• Acceso al sistema`}
        confirmText="Eliminar Usuario"
        cancelText="Cancelar"
        isDangerous={true}
      />

      {/* Modal de Confirmación de Eliminación de Expediente */}
      <ConfirmModal
        isOpen={deleteExpedienteModal.isOpen}
        onClose={() => setDeleteExpedienteModal({ isOpen: false, expediente: null })}
        onConfirm={handleDeleteExpediente}
        title="Eliminar Expediente"
        message={
          deleteExpedienteModal.expediente && (
            <div className="space-y-3">
              <p className="text-gray-300">
                ¿Estás seguro de que deseas eliminar este expediente permanentemente?
              </p>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <p className="text-red-300 text-sm font-semibold">
                  ⚠️ Esta acción no se puede deshacer
                </p>
              </div>
              <div className="bg-gray-700 p-3 rounded">
                <p className="text-white text-sm"><strong>Expediente:</strong> {deleteExpedienteModal.expediente.numero_expediente}</p>
                <p className="text-white text-sm"><strong>Cliente:</strong> {deleteExpedienteModal.expediente.solicitante.nombre_completo}</p>
                <p className="text-white text-sm"><strong>Cédula:</strong> {deleteExpedienteModal.expediente.solicitante.cedula}</p>
              </div>
            </div>
          )
        }
        confirmText={deletingExpediente ? "Eliminando..." : "Sí, Eliminar Expediente"}
        cancelText="Cancelar"
        isDangerous={true}
      />
    </div>
  );
}

function StatCard({ title, value, icon, color }) {
  return (
    <Card className="bg-gray-800/50 border-gray-700">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-400 mb-1">{title}</p>
            <p className="text-3xl font-bold text-white">{value}</p>
          </div>
          <div className={`${color} p-3 rounded-xl`}>{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function ExpedienteCard({ expediente, detailed = false, navigate, onDelete, isAdmin = false }) {
  const config = ESTADO_CONFIG[expediente.estado];
  const IconComponent = config?.icon || AlertCircle;

  const handleClick = (e) => {
    // No navegar si se hizo click en el botón de eliminar
    if (e.target.closest('button')) return;
    
    if (navigate) {
      navigate(`/expediente/${expediente.id}`);
    }
  };

  return (
    <div 
      onClick={handleClick}
      className="p-6 bg-gray-900/50 rounded-xl border border-gray-700 hover:border-yellow-500/50 transition-colors cursor-pointer relative"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h4 className="font-semibold text-white text-lg">{expediente.solicitante.nombre_completo}</h4>
          <p className="text-sm text-gray-400">Cédula: {expediente.solicitante.cedula}</p>
          <p className="text-sm text-gray-400">Email: {expediente.solicitante.email}</p>
          <p className="text-xs text-gray-500 mt-2">Exp. #{expediente.numero_expediente || expediente.id.slice(0, 8)}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`${config?.color} text-white flex items-center gap-2`}>
            <IconComponent className="w-4 h-4" />
            {config?.label}
          </Badge>
          {isAdmin && onDelete && (
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(expediente);
              }}
              className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
      {detailed && expediente.historial && expediente.historial.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          <h5 className="text-sm font-semibold text-gray-300 mb-2">Historial</h5>
          <div className="space-y-2">
            {expediente.historial.slice(-3).map((h, i) => (
              <div key={i} className="text-xs text-gray-400 p-2 bg-gray-800/50 rounded">
                <p><strong>{h.accion}</strong></p>
                <p>{formatearFechaHoraCaracas(h.fecha)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UsuarioCard({ usuario, onRoleChange, onToggleActive, onResetPassword, onEliminar, currentUserId }) {
  const [selectedRole, setSelectedRole] = useState(usuario.role);
  const isCurrentUser = usuario.id === currentUserId;
  const isBlocked = usuario.active === false;

  const handleRoleChange = (newRole) => {
    setSelectedRole(newRole);
    onRoleChange(usuario.id, newRole);
  };

  const handleToggleActive = () => {
    onToggleActive(usuario.id);
  };

  const handleResetPassword = () => {
    onResetPassword(usuario.id);
  };

  const handleEliminar = () => {
    onEliminar(usuario.id, usuario.name);
  };

  return (
    <div className={`p-6 bg-gray-900/50 rounded-xl border ${isBlocked ? 'border-red-500/50 opacity-60' : 'border-gray-700 hover:border-yellow-500/50'} transition-colors`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 ${isBlocked ? 'bg-red-500/10 border-red-500/20' : 'bg-yellow-500/10 border-yellow-500/20'} border rounded-full flex items-center justify-center`}>
            <UserCog className={`w-6 h-6 ${isBlocked ? 'text-red-500' : 'text-yellow-500'}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-white text-lg">{usuario.name}</h4>
              {isBlocked && (
                <Badge className="bg-red-500">Bloqueado</Badge>
              )}
            </div>
            <p className="text-sm text-gray-400">{usuario.email}</p>
            {usuario.username && (
              <p className="text-xs text-gray-500">Usuario: {usuario.username}</p>
            )}
            {isCurrentUser && (
              <Badge className="mt-1 bg-blue-500">Tú</Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!isCurrentUser && usuario.username && (
            <Button
              onClick={handleResetPassword}
              variant="outline"
              size="sm"
              className="border-orange-500 text-orange-500 hover:bg-orange-500/10"
              disabled={isBlocked}
            >
              Resetear Password
            </Button>
          )}
          {!isCurrentUser && (
            <>
              <Button
                onClick={handleToggleActive}
                variant="outline"
                size="sm"
                className={`${isBlocked ? 'border-green-500 text-green-500 hover:bg-green-500/10' : 'border-red-500 text-red-500 hover:bg-red-500/10'}`}
              >
                {isBlocked ? 'Activar' : 'Bloquear'}
              </Button>
              <Button
                onClick={handleEliminar}
                variant="outline"
                size="sm"
                className="border-red-600 text-red-600 hover:bg-red-600/10"
                title="Eliminar usuario permanentemente"
              >
                Eliminar
              </Button>
            </>
          )}
          <Select value={selectedRole} onValueChange={handleRoleChange} disabled={isCurrentUser || isBlocked}>
            <SelectTrigger className="w-40 border-gray-700 bg-gray-800 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              <SelectItem value="admin" className="text-white hover:bg-gray-700">Administrador</SelectItem>
              <SelectItem value="gerencial" className="text-white hover:bg-gray-700">Gerencial</SelectItem>
              <SelectItem value="financiero" className="text-white hover:bg-gray-700">Financiero</SelectItem>
              <SelectItem value="operador" className="text-white hover:bg-gray-700">Operador</SelectItem>
            </SelectContent>
          </Select>
          <Badge className={getRoleBadgeColor(usuario.role)}>
            {ROLE_LABELS[usuario.role]}
          </Badge>
        </div>
      </div>
    </div>
  );
}

function getRoleBadgeColor(role) {
  switch(role) {
    case 'admin': return 'bg-red-500';
    case 'gerencial': return 'bg-purple-500';
    case 'financiero': return 'bg-blue-500';
    case 'operador': return 'bg-green-500';
    default: return 'bg-gray-500';
  }
}


function CrearUsuarioForm({ onUsuarioCreado }) {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    name: '',
    email: '',
    role: 'operador'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateUsername = (username) => {
    if (username.length < 7) return 'Mínimo 7 caracteres';
    const letras = (username.match(/[a-zA-Z]/g) || []).length;
    const numeros = (username.match(/[0-9]/g) || []).length;
    if (letras < 6) return 'Debe tener al menos 6 letras';
    if (numeros < 1) return 'Debe tener al menos 1 número';
    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Limpiar errores cuando el usuario escribe
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    const newErrors = {};
    
    const usernameError = validateUsername(formData.username);
    if (usernameError) newErrors.username = usernameError;
    
    if (formData.password.length < 6) {
      newErrors.password = 'Mínimo 6 caracteres';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }
    
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }
    
    if (!formData.email.trim() || !formData.email.includes('@')) {
      newErrors.email = 'Email inválido';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/usuarios/crear`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
          name: formData.name,
          email: formData.email,
          role: formData.role
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`Usuario ${formData.username} creado exitosamente`);
        setFormData({
          username: '',
          password: '',
          confirmPassword: '',
          name: '',
          email: '',
          role: 'operador'
        });
        onUsuarioCreado();
      } else {
        toast.error(data.detail || 'Error al crear usuario');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Usuario *
        </label>
        <Input
          type="text"
          name="username"
          value={formData.username}
          onChange={handleChange}
          placeholder="ej: operador123"
          className="bg-gray-700/50 border-gray-600 text-white"
          disabled={loading}
        />
        {errors.username && (
          <p className="text-red-500 text-xs mt-1">{errors.username}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">Mínimo 6 letras y 1 número</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Nombre Completo *
        </label>
        <Input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="ej: Juan Pérez"
          className="bg-gray-700/50 border-gray-600 text-white"
          disabled={loading}
        />
        {errors.name && (
          <p className="text-red-500 text-xs mt-1">{errors.name}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Email *
        </label>
        <Input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="ejemplo@kompa.com"
          className="bg-gray-700/50 border-gray-600 text-white"
          disabled={loading}
        />
        {errors.email && (
          <p className="text-red-500 text-xs mt-1">{errors.email}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Rol *
        </label>
        <Select 
          value={formData.role} 
          onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
          disabled={loading}
        >
          <SelectTrigger className="bg-gray-700/50 border-gray-600 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-700">
            <SelectItem value="operador" className="text-white hover:bg-gray-700">Operador</SelectItem>
            <SelectItem value="gerencial" className="text-white hover:bg-gray-700">Gerencial</SelectItem>
            <SelectItem value="financiero" className="text-white hover:bg-gray-700">Financiero</SelectItem>
            <SelectItem value="admin" className="text-white hover:bg-gray-700">Administrador</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Contraseña *
        </label>
        <Input
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          placeholder="Mínimo 6 caracteres"
          className="bg-gray-700/50 border-gray-600 text-white"
          disabled={loading}
        />
        {errors.password && (
          <p className="text-red-500 text-xs mt-1">{errors.password}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Confirmar Contraseña *
        </label>
        <Input
          type="password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          placeholder="Repite la contraseña"
          className="bg-gray-700/50 border-gray-600 text-white"
          disabled={loading}
        />
        {errors.confirmPassword && (
          <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
        )}
      </div>

      <div className="md:col-span-2">
        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold h-12"
        >
          {loading ? 'Creando...' : 'Crear Usuario'}
        </Button>
      </div>
    </form>
  );
}

